import React, { useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import * as d3 from 'd3';
import { formatChartMetricValue, formatChartThresholdValue, compactMetricNumber } from './utils_incident';

const buildXLabels = (data) => {
  const seen = {};
  return data.map((d) => {
    const dateKey = d3.timeFormat('%d-%b')(d.x);
    const timeStr = d3.timeFormat('%H:%M')(d.x);
    if (seen[dateKey] === undefined) {
      seen[dateKey] = 0;
      return dateKey;
    }
    seen[dateKey]++;
    return timeStr;
  });
};

const parseHeight = (height) => {
  if (typeof height === 'number' && height > 0) {
    return { css: `${height}px`, px: height };
  }
  const px = parseInt(height, 10);
  if (px > 0) {
    return { css: typeof height === 'string' && height.endsWith('px') ? height : `${px}px`, px };
  }
  return { css: '145px', px: 145 };
};

/** Hero chart slot width ratio: 1→50%, 2→25%, 3→16.67%, 4→12.5%, 5–7→10% of chart width */
const getHeroBarWidthRatio = (barCount) => {
  if (barCount <= 0) return 0.1;
  if (barCount >= 5) return 0.10;
  return 0.50 / barCount;
};

const usesHeroPointLayout = (heroBarLayout, type) =>
  heroBarLayout && (type === 'bar' || type === 'area' || type === 'line');

const formatYAxisTick = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '0';
  if (Math.abs(numericValue) >= 1e4) {
    return compactMetricNumber(numericValue, 1) ?? '0';
  }
  if (Number.isInteger(numericValue)) {
    return numericValue.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }
  return numericValue.toLocaleString(undefined, { maximumFractionDigits: 1 });
};

const drawRoundedTopRect = (ctx, x, y, w, h, radius) => {
  if (w <= 0 || h <= 0) return;
  const r = Math.min(radius, w / 2, h);
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(x, y, w, h, [r, r, 0, 0]);
    return;
  }
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h);
  ctx.lineTo(x, y + h);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

const getBarTopRadius = (barWidth, useHeroBarLayout) => {
  const cap = useHeroBarLayout ? 6 : 4;
  return Math.min(cap, barWidth / 2);
};

const traceSvgPath = (ctx, pathD, close = false) => {
  const svgPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  svgPath.setAttribute('d', pathD);
  const total = svgPath.getTotalLength();
  if (!total) return false;

  ctx.beginPath();
  const steps = Math.max(24, Math.ceil(total / 2));
  for (let i = 0; i <= steps; i += 1) {
    const point = svgPath.getPointAtLength((total * i) / steps);
    if (i === 0) ctx.moveTo(point.x, point.y);
    else ctx.lineTo(point.x, point.y);
  }
  if (close) ctx.closePath();
  return true;
};

const fillSvgPath = (ctx, pathD) => {
  if (!pathD) return;
  if (typeof Path2D !== 'undefined') {
    try {
      ctx.fill(new Path2D(pathD));
      return;
    } catch (e) {
      // Fall through to SVG sampling.
    }
  }
  if (traceSvgPath(ctx, pathD, true)) ctx.fill();
};

const strokeSvgPath = (ctx, pathD) => {
  if (!pathD) return;
  if (typeof Path2D !== 'undefined') {
    try {
      ctx.stroke(new Path2D(pathD));
      return;
    } catch (e) {
      // Fall through to SVG sampling.
    }
  }
  if (traceSvgPath(ctx, pathD, false)) ctx.stroke();
};

const VerticalChart = ({
  xValues = [],
  yValues = [],
  zValues = [],
  driftValues = [],
  previousAvgValues = [],
  type = 'area',
  showYAxis = false,
  showXAxis = true,
  enableTooltip = true,
  units = '',
  height = 145,
  axisFontSize = 9,
  heroBarLayout = false,
  resizeKey = 0,
  compareIndex = null,
}) => {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const tooltipRef = useRef(null);
  const chartStateRef = useRef(null);
  const unitsRef = useRef(units);
  const { css: heightCss, px: heightPx } = parseHeight(height);

  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  unitsRef.current = units;

  const measureDimensions = useCallback(() => {
    const target = wrapperRef.current;
    if (!target) return;

    const rect = target.getBoundingClientRect();
    const parentRect = target.parentElement?.getBoundingClientRect();
    // Prefer the largest available width so flex-centered parents don't leave side gaps.
    const width = Math.floor(Math.max(rect.width || 0, parentRect?.width || 0));
    const heightValue = Math.floor(rect.height) || heightPx;

    if (width > 0 && heightValue > 0) {
      setDimensions((prev) => (
        prev.width === width && prev.height === heightValue
          ? prev
          : { width, height: heightValue }
      ));
    }
  }, [heightPx]);

  useEffect(() => {
    const target = wrapperRef.current;
    if (!target) return undefined;
    const parent = target.parentElement;

    measureDimensions();

    const resizeObserver = new ResizeObserver(() => {
      measureDimensions();
    });
    resizeObserver.observe(target);
    if (parent) {
      resizeObserver.observe(parent);
    }

    const intersectionObserver = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        requestAnimationFrame(measureDimensions);
      }
    }, { threshold: 0.01 });
    intersectionObserver.observe(target);

    const rafId = requestAnimationFrame(measureDimensions);

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
    };
  }, [measureDimensions, resizeKey, heightPx]);

  const hideTooltip = useCallback(() => {
    const tip = tooltipRef.current;
    if (tip) tip.style.opacity = '0';
  }, []);

  const showTooltip = useCallback((event, point, index, threshold, highlightIdx) => {
    const tip = tooltipRef.current;
    if (!tip) return;

    const OFFSET = 12;
    const shortDate = d3.timeFormat('%b %d')(point.x);
    const fullDate = d3.timeFormat('%Y-%m-%d %H:%M:%S')(point.x);
    const formattedValue = formatChartMetricValue(point.y, unitsRef.current, { maximumFractionDigits: 2 });
    const thresholdRow = threshold != null
      ? `<dt><p>Drift Threshold: ${formatChartThresholdValue(threshold)}</p></dt>`
      : '';
    const previousAvgRow = point.previousAvg != null && Number.isFinite(point.previousAvg)
      ? `<dt><p>Previous Avg: ${formatChartMetricValue(point.previousAvg, unitsRef.current, { maximumFractionDigits: 2 })}</p></dt>`
      : '';
    const driftIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FECDCA" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;vertical-align:-2px;margin-right:4px" aria-hidden="true"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"></path><path d="M12 9v4"></path><path d="M12 17h.01"></path></svg>`;
    const driftRow = point.drift != null && Number.isFinite(point.drift)
      ? `<dt><p style="color:#FECDCA">${driftIcon}Current Drift: ${formatChartThresholdValue(point.drift)}</p></dt>`
      : '';

    tip.innerHTML = `
      <div class="qd-incidents-tooltip">
        <div class="qd-incidents-tooltip__dialog">
          <h6>${shortDate}</h6>
          <small class="font-light">${fullDate} GMT</small>
          <dl>
            ${thresholdRow}
            ${previousAvgRow}
            ${driftRow}
            <dd>
              <p>
                <span class="dot ${index === highlightIdx ? 'text-danger' : ''}"></span>
                Value: ${formattedValue}
              </p>
            </dd>
          </dl>
        </div>
      </div>
    `;

    // Measure after content is set; position in viewport so parents with
    // overflow:hidden / transform (hero carousel) cannot clip the tooltip.
    tip.style.opacity = '0';
    tip.style.left = '0px';
    tip.style.top = '0px';
    const tipW = tip.offsetWidth || 200;
    const tipH = tip.offsetHeight || 80;

    let left = event.clientX + OFFSET;
    let top = event.clientY - tipH - OFFSET;

    if (left + tipW > window.innerWidth - 8) {
      left = event.clientX - tipW - OFFSET;
    }
    if (left < 8) {
      left = 8;
    }
    if (top < 8) {
      top = event.clientY + OFFSET;
    }
    if (top + tipH > window.innerHeight - 8) {
      top = Math.max(8, window.innerHeight - tipH - 8);
    }

    tip.style.left = `${left}px`;
    tip.style.top = `${top}px`;
    tip.style.opacity = '1';
  }, []);

  const drawChart = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !dimensions.width || !dimensions.height) return;

    const xs = Array.isArray(xValues) ? xValues : [];
    const ys = Array.isArray(yValues) ? yValues : [];
    const drifts = Array.isArray(driftValues) ? driftValues : [];
    const previousAvgs = Array.isArray(previousAvgValues) ? previousAvgValues : [];
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const fullWidth = dimensions.width;
    const fullHeight = dimensions.height;

    canvas.width = Math.max(1, Math.floor(fullWidth * dpr));
    canvas.height = Math.max(1, Math.floor(fullHeight * dpr));
    canvas.style.width = `${fullWidth}px`;
    canvas.style.height = `${fullHeight}px`;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, fullWidth, fullHeight);

    if (!xs.length || !ys.length) {
      chartStateRef.current = null;
      return;
    }

    const parseX = (v) => {
      if (v instanceof Date) return v;
      if (typeof v === 'number' && Number.isFinite(v)) {
        const fromNumber = new Date(v);
        return isNaN(fromNumber.valueOf()) ? null : fromNumber;
      }
      if (v == null || v === '') return null;
      const raw = String(v).trim();
      // API may send microsecond fractions (e.g. .000000) — keep only milliseconds for Date.parse.
      const normalized = raw
        .replace(/(\.\d{3})\d+/, '$1')
        .replace(' ', 'T');
      const parsed = new Date(normalized);
      if (!isNaN(parsed.valueOf())) return parsed;
      const fallback = new Date(raw);
      return isNaN(fallback.valueOf()) ? null : fallback;
    };

    const toFiniteNumber = (value) => {
      if (value == null || value === '') return null;
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    };

    const data = xs.map((x, i) => {
      const parsedX = parseX(x);
      return {
        // Keep single-point series plottable even if timestamp parsing fails.
        x: parsedX || new Date(Date.UTC(1970, 0, 1 + i)),
        y: Number(ys[i] ?? 0),
        originalIndex: i,
        drift: toFiniteNumber(drifts[i]),
        previousAvg: toFiniteNumber(previousAvgs[i]),
      };
    }).filter((d) => Number.isFinite(d.y));

    if (!data.length) {
      chartStateRef.current = null;
      return;
    }

    data.sort((a, b) => a.x - b.x);

    const parsedCompareIndex = Number(compareIndex);
    const hasCompareIndex = Number.isInteger(parsedCompareIndex) && parsedCompareIndex >= 0;
    const compareSortedIdx = hasCompareIndex
      ? data.findIndex((d) => d.originalIndex === parsedCompareIndex)
      : -1;
    const highlightIdx = compareSortedIdx >= 0 ? compareSortedIdx : data.length - 1;
    const rawZ = Array.isArray(zValues) ? zValues[0] : zValues;
    const threshold = rawZ != null && !isNaN(Number(rawZ)) ? Number(rawZ) : null;

    const yMax = d3.max(data, (d) => d.y) ?? 0;
    const yMin = d3.min(data, (d) => d.y) ?? 0;
    const pad = Math.max((yMax - yMin) * 0.25, 5);
    const domainMax = Math.max(yMax, threshold ?? 0) + pad;
    const domainMin = Math.max(0, Math.min(yMin, threshold ?? yMin) - pad / 2);

    const fontSize = Number(axisFontSize) > 0 ? Number(axisFontSize) : 9;
    const yTicks = showYAxis
      ? d3.scaleLinear().domain([domainMin, domainMax]).nice().ticks(5)
      : [];

    let leftMargin = 0;
    if (showYAxis && yTicks.length) {
      ctx.font = `${fontSize}px sans-serif`;
      const maxLabelWidth = yTicks.reduce((max, tick) => (
        Math.max(max, ctx.measureText(formatYAxisTick(tick)).width)
      ), 0);
      leftMargin = Math.max(40, Math.ceil(maxLabelWidth) + 10);
    }

    const margin = {
      top: 14,
      right: showYAxis ? 8 : 0,
      bottom: showXAxis ? 28 : 8,
      left: leftMargin,
    };
    const plotWidth = fullWidth - margin.left - margin.right;
    const plotHeight = fullHeight - margin.top - margin.bottom;

    if (plotWidth <= 0 || plotHeight <= 0) {
      chartStateRef.current = null;
      return;
    }

    const xScale = d3.scaleBand()
      .domain(data.map((_, i) => i))
      .range([0, plotWidth])
      .paddingInner(0.18)
      .paddingOuter(0);

    const useHeroBarLayout = usesHeroPointLayout(heroBarLayout, type);
    const barCount = data.length;
    const heroSlotWidth = useHeroBarLayout ? plotWidth / barCount : 0;

    const MIN_BAR = 4;
    const MAX_BAR = 28;
    const barWidth = useHeroBarLayout
      ? plotWidth * getHeroBarWidthRatio(barCount)
      : Math.min(MAX_BAR, Math.max(MIN_BAR, xScale.bandwidth()));

    const getBarX = (i) => (useHeroBarLayout
      ? i * heroSlotWidth + (heroSlotWidth - barWidth) / 2
      : xScale(i) + (xScale.bandwidth() - barWidth) / 2);

    const getCenterX = (i) => (useHeroBarLayout
      ? i * heroSlotWidth + heroSlotWidth / 2
      : xScale(i) + xScale.bandwidth() / 2);

    const getSlotX = (i) => (useHeroBarLayout ? i * heroSlotWidth : xScale(i));
    const slotWidth = useHeroBarLayout ? heroSlotWidth : xScale.bandwidth();

    const yScale = d3.scaleLinear()
      .domain([domainMin, domainMax])
      .nice()
      .range([plotHeight, 0]);

    const xLabels = buildXLabels(data);

    ctx.save();
    ctx.translate(margin.left, margin.top);

    if (showYAxis) {
      yTicks.forEach((tick) => {
        const y = yScale(tick);
        ctx.strokeStyle = 'rgba(255,255,255,0.06)';
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(plotWidth, y);
        ctx.stroke();
        ctx.setLineDash([]);

        ctx.fillStyle = '#94979C';
        ctx.font = `${fontSize}px sans-serif`;
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillText(formatYAxisTick(tick), -8, y);
      });
    }

    ctx.strokeStyle = 'rgba(255,255,255,0.08)';
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(0, plotHeight);
    ctx.lineTo(plotWidth, plotHeight);
    ctx.stroke();

    if (type === 'bar') {
      const barRadius = getBarTopRadius(barWidth, useHeroBarLayout);
      data.forEach((d, i) => {
        const x = getBarX(i);
        const y = yScale(d.y);
        const h = plotHeight - y;

        drawRoundedTopRect(ctx, x, y, barWidth, h, barRadius);

        if (i === highlightIdx) {
          const grad = ctx.createLinearGradient(x, y + h, x, y);
          grad.addColorStop(0, '#F64E60');
          grad.addColorStop(0.7, '#F87180');
          grad.addColorStop(1, '#FEEDEF');
          ctx.fillStyle = grad;
        } else {
          ctx.fillStyle = '#5D5B74';
        }

        ctx.fill();
      });
    } else {
      const lineGen = d3.line()
        .x((_, i) => getCenterX(i))
        .y((d) => yScale(d.y))
        .curve(d3.curveMonotoneX);

      if (type === 'area') {
        const areaGen = d3.area()
          .x((_, i) => getCenterX(i))
          .y0(plotHeight)
          .y1((d) => yScale(d.y))
          .curve(d3.curveMonotoneX);

        const areaPath = areaGen(data);
        if (areaPath) {
          const grad = ctx.createLinearGradient(0, 0, 0, plotHeight);
          grad.addColorStop(0, 'rgba(93, 91, 116, 0.45)');
          grad.addColorStop(1, 'rgba(93, 91, 116, 0)');
          ctx.fillStyle = grad;
          fillSvgPath(ctx, areaPath);
        }
      }

      const linePath = lineGen(data);
      if (linePath) {
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';

        if (data.length > 1) {
          const redStartX = highlightIdx > 0 ? getCenterX(highlightIdx - 1) : Math.max(0, getCenterX(0) - slotWidth / 2);
          const redEndX = highlightIdx < data.length - 1
            ? getCenterX(highlightIdx)
            : plotWidth + margin.right + 4;
          const redWidth = Math.max(slotWidth, redEndX - redStartX + 4);

          ctx.strokeStyle = '#5D5B74';
          ctx.lineWidth = 2;
          strokeSvgPath(ctx, linePath);

          ctx.save();
          ctx.beginPath();
          ctx.rect(redStartX, -margin.top, redWidth, plotHeight + margin.top + 30);
          ctx.clip();

          ctx.strokeStyle = '#E04757';
          ctx.lineWidth = 2.5;
          strokeSvgPath(ctx, linePath);
          ctx.restore();
        } else {
          ctx.strokeStyle = '#E04757';
          ctx.lineWidth = 2;
          strokeSvgPath(ctx, linePath);
        }
      }

      data.forEach((d, i) => {
        const cx = getCenterX(i);
        const cy = yScale(d.y);
        const r = i === highlightIdx ? 5 : 3;

        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fillStyle = i === highlightIdx ? '#E04757' : '#5D5B74';
        ctx.fill();
      });

      const highlightCx = getCenterX(highlightIdx);
      const highlightCy = yScale(data[highlightIdx].y);
      ctx.beginPath();
      ctx.arc(highlightCx, highlightCy, 8, 0, Math.PI * 2);
      ctx.strokeStyle = '#F04438';
      ctx.lineWidth = 1.5;
      ctx.globalAlpha = 0.6;
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    if (threshold != null) {
      const ty = yScale(threshold);
      if (ty >= 0 && ty <= plotHeight) {
        ctx.strokeStyle = 'rgba(148, 151, 156, 0.55)';
        ctx.lineWidth = 1;
        ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.moveTo(0, ty);
        ctx.lineTo(plotWidth, ty);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }

    if (showXAxis) {
      ctx.fillStyle = '#94979C';
      ctx.font = `${fontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      data.forEach((_, i) => {
        ctx.fillText(xLabels[i], getCenterX(i), plotHeight + 6);
      });
    }

    ctx.restore();

    chartStateRef.current = {
      data,
      margin,
      plotWidth,
      plotHeight,
      getSlotX,
      slotWidth,
      threshold,
      highlightIdx,
    };
  }, [xValues, yValues, zValues, driftValues, previousAvgValues, dimensions, type, showYAxis, showXAxis, axisFontSize, heroBarLayout, compareIndex]);

  useEffect(() => {
    drawChart();
  }, [drawChart, resizeKey]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || !enableTooltip) return undefined;

    const handleMouseMove = (event) => {
      const state = chartStateRef.current;
      if (!state) return;

      const rect = wrapper.getBoundingClientRect();
      const mouseX = event.clientX - rect.left - state.margin.left;
      const mouseY = event.clientY - rect.top - state.margin.top;

      if (mouseX < 0 || mouseX > state.plotWidth || mouseY < 0 || mouseY > state.plotHeight) {
        hideTooltip();
        wrapper.style.cursor = 'default';
        return;
      }

      let hoveredIndex = -1;
      state.data.forEach((point, i) => {
        const slotX = state.getSlotX(i);
        if (mouseX >= slotX && mouseX <= slotX + state.slotWidth) {
          hoveredIndex = i;
        }
      });

      if (hoveredIndex !== -1) {
        wrapper.style.cursor = 'pointer';
        showTooltip(
          event,
          state.data[hoveredIndex],
          hoveredIndex,
          state.threshold,
          state.highlightIdx,
        );
      } else {
        wrapper.style.cursor = 'default';
        hideTooltip();
      }
    };

    const handleMouseLeave = () => {
      hideTooltip();
      wrapper.style.cursor = 'default';
    };

    wrapper.addEventListener('mousemove', handleMouseMove);
    wrapper.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      hideTooltip();
      wrapper.removeEventListener('mousemove', handleMouseMove);
      wrapper.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [hideTooltip, showTooltip, enableTooltip]);

  return (
    <div
      ref={wrapperRef}
      className="qd-incidents-chart-host"
      style={{
        width: '100%',
        minWidth: 0,
        height: heightCss,
        position: 'relative',
        display: 'block',
        alignSelf: 'stretch',
        zIndex: 1,
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: heightCss }} />
      {enableTooltip && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={tooltipRef}
              className="qd-incidents-tooltip-wrap"
              style={{
                position: 'fixed',
                pointerEvents: 'none',
                opacity: 0,
                zIndex: 100000,
                transition: 'opacity 0.1s ease-out',
              }}
            />,
            document.body
          )
        : null}
    </div>
  );
};

export default VerticalChart;
